import { Static, Type } from '@sinclair/typebox';

export enum StripeConnectAccountStatus {
  NOT_CREATED = 'NOT_CREATED',
  ONBOARDING_PENDING = 'ONBOARDING_PENDING',
  ACTIVE = 'ACTIVE',
  RESTRICTED = 'RESTRICTED',
  DISABLED = 'DISABLED',
}

/**
 * Stripe payment-method API tokens (lowercase), as used in Connect capabilities and in a
 * PaymentIntent's `payment_method_types`. Single source of truth shared across packages —
 * distinct from the DB `PaymentMethod` enum (CARD/SEPA_DEBIT). Use this enum instead of raw
 * string literals when referencing Stripe payment-method tokens.
 */
export enum StripePaymentMethodToken {
  CARD = 'card',
  SEPA_DEBIT = 'sepa_debit',
  BANCONTACT = 'bancontact',
  LINK = 'link',
  KLARNA = 'klarna',
}

/** A payment method the platform both charges and displays (see SUPPORTED_PAYMENT_METHOD_TOKENS). */
export type SupportedPaymentMethodToken = StripePaymentMethodToken.CARD | StripePaymentMethodToken.KLARNA;

/**
 * The ONE list of payment methods the platform supports end to end (decision 2026-09-16): it is
 * both what a checkout may charge and what the tunnels display (brand logos). A method the pro
 * activated on Stripe but absent here is neither charged nor shown, and triggers a kchat alert
 * when it first becomes active. The wider enum above stays: it maps Connect capabilities.
 */
export const SUPPORTED_PAYMENT_METHOD_TOKENS: readonly SupportedPaymentMethodToken[] = [
  StripePaymentMethodToken.CARD,
  StripePaymentMethodToken.KLARNA,
];

/** True when the platform charges and displays this Stripe payment-method token. */
export function isSupportedPaymentMethod(token: string): token is SupportedPaymentMethodToken {
  return (SUPPORTED_PAYMENT_METHOD_TOKENS as readonly string[]).includes(token);
}

// --- Response: account status ---
export const StripeConnectAccountStatusResponseSchema = Type.Object({
  status: Type.Enum(StripeConnectAccountStatus),
  stripe_connect_account_id: Type.Optional(Type.String()),
  charges_enabled: Type.Boolean(),
  payouts_enabled: Type.Boolean(),
  details_submitted: Type.Boolean(),
  requirements_currently_due: Type.Array(Type.String()),
  dashboard_login_url: Type.Optional(Type.String()),
});
export type StripeConnectAccountStatusResponseObject = Static<typeof StripeConnectAccountStatusResponseSchema>;

// --- Response: account link (onboarding URL) ---
export const StripeConnectAccountLinkResponseSchema = Type.Object({
  url: Type.String(),
});
export type StripeConnectAccountLinkResponseObject = Static<typeof StripeConnectAccountLinkResponseSchema>;

export enum StripeConnectPayoutStatus {
  PAID = 'PAID',
  PENDING = 'PENDING',
  IN_TRANSIT = 'IN_TRANSIT',
  CANCELED = 'CANCELED',
  FAILED = 'FAILED',
}

export enum StripeConnectPayoutInterval {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  MANUAL = 'MANUAL',
}

// --- Response: payout overview (live snapshot from Stripe, nothing stored) ---
export const StripeConnectPayoutSnapshotSchema = Type.Object({
  amount: Type.String({ meta: { is_price: true, source_key: 'amount_raw' } }),
  arrival_date_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'arrival_date' } })),
  status: Type.Enum(StripeConnectPayoutStatus),
});
export type StripeConnectPayoutSnapshotObject = Static<typeof StripeConnectPayoutSnapshotSchema>;

export const StripeConnectPayoutOverviewResponseSchema = Type.Object({
  last_payout: Type.Optional(StripeConnectPayoutSnapshotSchema),
  next_payout: Type.Optional(StripeConnectPayoutSnapshotSchema),
  balance_available: Type.String({ meta: { is_price: true, source_key: 'balance_available_raw' } }),
  balance_pending: Type.String({ meta: { is_price: true, source_key: 'balance_pending_raw' } }),
  payout_interval: Type.Optional(Type.Enum(StripeConnectPayoutInterval)),
});
export type StripeConnectPayoutOverviewResponseObject = Static<typeof StripeConnectPayoutOverviewResponseSchema>;

// --- Request: create account link ---
export const StripeConnectCreateAccountLinkRequestSchema = Type.Object({
  refresh_url: Type.String(),
  return_url: Type.String(),
});
export type StripeConnectCreateAccountLinkRequestObject = Static<typeof StripeConnectCreateAccountLinkRequestSchema>;

// --- Request: create account ---
// URLs optionnelles : Stripe renvoie l'utilisateur sur la page qui a lancé
// l'onboarding (fallback backend sur /sales si absentes).
export const StripeConnectCreateAccountRequestSchema = Type.Object({
  refresh_url: Type.Optional(Type.String()),
  return_url: Type.Optional(Type.String()),
});
export type StripeConnectCreateAccountRequestObject = Static<typeof StripeConnectCreateAccountRequestSchema>;
